import {
    Component,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    input,
    computed,
    effect,
    ElementRef,
    model,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow } from "./service/Workflow";
import { WorkflowUtil } from "./workflow.util";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";
import { ExpandTrigger, WFError, WorkflowSteps } from "./workflow.process.types";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25Util } from "../../util/s25-util";

@TypeManagerDecorator("workflow-stage-transitions")
@Component({
    selector: "workflow-stage-transitions",
    template: `
        <div class="transitions-container">
            @if (error()?.message) {
                <div class="ngRed" [innerHTML]="error()!.message"></div>
            }

            <div class="transitions-header">
                <h3>Transitions</h3>
                <button class="aw-button aw-button--primary" (click)="addTransition()">Add Transition</button>
            </div>

            <div class="transitions-list">
                @for (
                    transition of workflowProcess().stageTransitions;
                    track transition.workflowStageTransitionId || transition.id
                ) {
                    <div
                        class="transition-item"
                        [class.error-index]="errorAtIndex($index)"
                        [attr.data-transition-id]="transition.workflowStageTransitionId || transition.id"
                    >
                        <div class="transition-main">
                            <div class="transition-info">
                                <div class="transition-label">
                                    <s25-ng-editable-text
                                        [val]="transition.transitionLabel"
                                        [hasCommit]="true"
                                        [hasCommitButton]="true"
                                        [hasCancelButton]="true"
                                        [customValidation]="getTransitionLabelValidation(transition)"
                                        [onCommit]="getTransitionLabelCommitHandler(transition)"
                                        [placeholder]="'+ Add Transition Label'"
                                        placeholder="Transition label"
                                        class="label-input"
                                    ></s25-ng-editable-text>
                                </div>

                                <div class="transition-flow">
                                    <select
                                        [(ngModel)]="transition.fromStageName"
                                        (ngModelChange)="onFromStageChange(transition, $event)"
                                        class="stage-select cn-form__control"
                                    >
                                        <option value="">From stage...</option>
                                        @for (stage of availableFromStages(); track stage.workflowStageId || stage.id) {
                                            <option [value]="stage.stageName">
                                                {{ stage.stageName }}{{ stage.isTerminal ? " (Terminal)" : "" }}
                                            </option>
                                        }
                                    </select>

                                    <div class="transition-arrow">
                                        <select
                                            [(ngModel)]="transition.transitionType"
                                            class="type-select cn-form__control"
                                        >
                                            <option value="APPROVE">Approve</option>
                                            <option value="DENY">Deny</option>
                                        </select>
                                    </div>

                                    <select
                                        [(ngModel)]="transition.toStageName"
                                        (ngModelChange)="onToStageChange(transition, $event)"
                                        class="stage-select cn-form__control"
                                    >
                                        <option value="">To stage...</option>
                                        @for (
                                            stage of availableToStages(transition);
                                            track stage.workflowStageId || stage.id
                                        ) {
                                            <option [value]="stage.stageName">
                                                {{ stage.stageName }}{{ stage.isTerminal ? " (Terminal)" : "" }}
                                            </option>
                                        }
                                    </select>
                                </div>
                            </div>
                            <div class="transition-actions">
                                <button
                                    class="btn btn-sm"
                                    [style.padding-inline]="'11px'"
                                    (click)="toggleTransitionCollapse(transition)"
                                >
                                    {{ isTransitionCollapsed(transition) ? "▼" : "▲" }}
                                </button>
                                <button
                                    class="btn btn-sm btn-danger"
                                    (click)="removeTransition(transition)"
                                    aria-label="Remove transition"
                                >
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>
                            </div>
                        </div>

                        @if (!isTransitionCollapsed(transition)) {
                            <div class="transition-details">
                                <div class="transition-description">
                                    <textarea
                                        [(ngModel)]="transition.description"
                                        placeholder="Description (optional)"
                                        rows="2"
                                    ></textarea>
                                </div>

                                @if (getTransitionValidationErrors(transition).length > 0) {
                                    <div class="validation-errors">
                                        @for (error of getTransitionValidationErrors(transition); track error) {
                                            <div class="error-message">{{ error }}</div>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            @if ((workflowProcess().stageTransitions || []).length === 0) {
                <div class="no-transitions">
                    <p>No transitions defined. Add transitions to connect your workflow stages.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .transitions-container {
            padding: 20px;
        }

        .transitions-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .transitions-header h3 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .transitions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .transition-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .transition-item.error-index {
            border-color: var(--color--danger);
        }

        .transition-main {
            padding: 16px;
            display: flex;
            gap: 16px;
            align-items: flex-start;
            background: var(--color--surface-2-bg);
        }

        .transition-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .transition-flow {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
            align-items: center;
            gap: 12px;
            width: 100%;
        }

        .stage-select.cn-form__control {
            width: 100%;
            min-width: 0;
            max-width: 100%;
        }

        .transition-arrow {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .type-select.cn-form__control {
            width: auto;
            min-width: 7.5rem;
            padding-left: 0.75em;
            padding-right: 2.25em;
            line-height: 2em;
            text-align: center;
            text-align-last: center;
            border-radius: var(--border-radius);
            border: 1px solid var(--color--link-text) !important;
            background-color: var(--color--link-text);
            color: var(--color--surface-bg);
            font-weight: 500;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
            background-position: right 0.35em center;
            background-repeat: no-repeat;
            background-size: 1.1em 1.1em;
        }

        .type-select.cn-form__control:not([disabled]):hover,
        .type-select.cn-form__control:focus {
            background-color: var(--color--link-text-hover) !important;
            border-color: var(--color--link-text-hover) !important;
            color: var(--color--surface-bg) !important;
            box-shadow: var(--shadow--form-focus);
        }

        .transition-label {
            width: fit-content;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            padding: 6px 10px;
            background: var(--color--surface-bg);
        }

        .transition-label:has(s25-ng-editable-text.editing) {
            width: 100%;
        }

        .label-input {
            width: 100%;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .transition-actions {
            display: flex;
            gap: 8px;
        }

        .transition-details {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .transition-description {
            margin-bottom: 16px;
        }

        .transition-description textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            resize: vertical;
        }

        .validation-errors {
            background: var(--color--warning);
            border: 1px solid var(--color--warning);
            border-radius: 4px;
            padding: 12px;
        }

        .error-message {
            color: var(--color--surface-bg);
            font-size: 14px;
            margin-bottom: 4px;
        }

        .error-message:last-child {
            margin-bottom: 0;
        }

        .no-transitions {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }

        .ngRed {
            font-size: 0.9rem;
        }

        ::ng-deep .transition-label s25-ng-editable-text.editing input {
            width: 100%;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, S25EditableTextComponent, S25IconComponent],
})
export class WorkflowStageTransitionsComponent {
    workflowProcess = input.required<Workflow.WorkflowProcess>();
    error = input<WFError>();
    selectedTransitionTrigger = model<ExpandTrigger>();
    @Output() transitionsChange = new EventEmitter<Workflow.WorkflowStageTransition[]>();

    private collapsedTransitions = new Set<number | string>();

    protected errorAtIndex = (index: number) => this.error()?.indices?.some((i) => i === index);

    // Return all stages for from selection except terminal stages
    protected availableFromStages = computed(() => (this.workflowProcess().stages || []).filter((s) => !s.isTerminal));

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            const wf = this.workflowProcess();
            // Use the utility function to populate stage names
            if (wf) {
                if (!wf.stageTransitions) wf.stageTransitions = [];
                WorkflowUtil.populateStageNamesOnTransitions(wf.stageTransitions, wf.stages || []);
            }
        });

        effect(() => {
            const expandTrigger = this.selectedTransitionTrigger();
            if (
                expandTrigger &&
                expandTrigger.step === WorkflowSteps.Transitions &&
                S25Util.isDefined(expandTrigger.id)
            ) {
                this.expandTransitionById(expandTrigger.id);
                this.selectedTransitionTrigger.set(undefined);
            }
        });
    }

    addTransition() {
        const newTransitionTempId =
            (this.workflowProcess().stageTransitions || []).reduce(
                (minId, transition) => Math.min(minId, transition.id || -1),
                -1,
            ) - 1;

        const newTransition: Workflow.WorkflowStageTransition = {
            id: newTransitionTempId,
            workflowProcessId: this.workflowProcess().workflowProcessId,
            transitionType: "APPROVE",
            transitionLabel: "",
            fromStageName: "",
            toStageName: "",
            description: "",
        };

        if (!this.workflowProcess().stageTransitions) {
            this.workflowProcess().stageTransitions = [];
        }
        this.workflowProcess().stageTransitions!.push(newTransition);
        this.emitTransitionsChange();
    }

    removeTransition(transitionToRemove: Workflow.WorkflowStageTransition) {
        if (!this.workflowProcess().stageTransitions) return;

        const index = this.workflowProcess().stageTransitions!.findIndex(
            (transition) =>
                (transition.workflowStageTransitionId || transition.id) ===
                (transitionToRemove.workflowStageTransitionId || transitionToRemove.id),
        );
        if (index !== -1) {
            this.workflowProcess().stageTransitions!.splice(index, 1);
            this.emitTransitionsChange();
        }
    }

    onFromStageChange(transition: Workflow.WorkflowStageTransition, stageName: string) {
        transition.fromStageName = stageName;

        const stage = this.workflowProcess().stages?.find((s) => s.stageName === stageName);
        transition.fromStageId = stage?.workflowStageId ?? undefined;

        this.emitTransitionsChange();
    }

    onToStageChange(transition: Workflow.WorkflowStageTransition, stageName: string) {
        transition.toStageName = stageName;

        const stage = this.workflowProcess().stages?.find((s) => s.stageName === stageName);
        transition.toStageId = stage?.workflowStageId ?? undefined;

        this.emitTransitionsChange();
    }

    availableToStages(transition: Workflow.WorkflowStageTransition) {
        // Return all stages except the one being transitioned from
        return (this.workflowProcess().stages || []).filter((s) => s.stageName !== transition.fromStageName);
    }

    getTransitionValidationErrors(transition: Workflow.WorkflowStageTransition): string[] {
        return WorkflowUtil.getTransitionValidationErrors(
            this.workflowProcess().stageTransitions || [],
            this.workflowProcess().stages || [],
            transition,
        );
    }

    toggleTransitionCollapse(transition: Workflow.WorkflowStageTransition) {
        const id = transition.workflowStageTransitionId || transition.id;
        if (S25Util.isDefined(id)) {
            if (this.collapsedTransitions.has(id)) {
                this.collapsedTransitions.delete(id);
            } else {
                this.collapsedTransitions.add(id);
            }
        }
    }

    isTransitionCollapsed(transition: Workflow.WorkflowStageTransition): boolean {
        const id = transition.workflowStageTransitionId || transition.id;
        if (S25Util.isDefined(id)) {
            return this.collapsedTransitions.has(id);
        }
        return false;
    }

    getTransitionLabelCommitHandler(transition: Workflow.WorkflowStageTransition) {
        return (newName: string) => {
            this.onTransitionLabelCommit(transition, newName);
        };
    }

    onTransitionLabelCommit(transition: Workflow.WorkflowStageTransition, newLabel: string) {
        transition.transitionLabel = newLabel;
        this.emitTransitionsChange();
    }

    getTransitionLabelValidation(transition: Workflow.WorkflowStageTransition) {
        return (inputVal: string) => {
            const trimmedLabel = inputVal?.trim();

            if (!trimmedLabel) {
                return ["Transition label is required"];
            }

            // Check for duplicate transition labels
            const isDuplicate = this.workflowProcess().stageTransitions!.some(
                (t) =>
                    t.transitionLabel === trimmedLabel &&
                    t !== transition &&
                    (t.workflowStageTransitionId !== transition.workflowStageTransitionId || t.id !== transition.id),
            );

            if (isDuplicate) {
                return [`Transition label "${trimmedLabel}" already exists. Please choose a unique name.`];
            }

            return true;
        };
    }

    private emitTransitionsChange() {
        this.cd.detectChanges();
        this.transitionsChange.emit(this.workflowProcess().stageTransitions || []);
    }

    expandTransitionById(transitionId: number | string) {
        const transitions = this.workflowProcess().stageTransitions;
        const transition = transitions?.find(
            (t) => t.workflowStageTransitionId === transitionId || t.id === transitionId,
        );
        if (transition) {
            const id = transition.workflowStageTransitionId || transition.id;
            // Remove from collapsed set to expand
            if (S25Util.isDefined(id) && this.collapsedTransitions.has(id)) {
                this.collapsedTransitions.delete(id);
            }
            // Scroll to the transition element
            setTimeout(() => {
                const transitionEl = this.el.nativeElement.querySelector(`[data-transition-id="${transitionId}"]`);
                transitionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }
}
